import { millisecondsToSeconds } from 'date-fns';
import { action, makeAutoObservable, observable, runInAction, set } from 'mobx';

import { type CurveInfo, type CurvePoint, CurvesCacheService } from '../../shared/api/CurvesCacheService';
import type { LogCurve } from '../../shared/api/IWellDirectoryService';
import type { CurvePaletteKey } from '../../shared/curvePalette';
import { type SocketCurveMessage, type WellLogWidgetStompPublisher } from '../../shared/stompTypes';
import { roundNumber } from '../../shared/utils';
import { radiansToDegrees } from '../angleUtils';
import { MAX_POINTS_QUANTITY, MIN_POINTS_QUANTITY } from '../consts';
import type { TemporarySettings, ToolSettings } from '../types';

import type { DirectionalDrillingStatePublisher } from './DirectionalDrillingStatePublisher';

export class ToolState {
  type: 'gravity' | 'magnetic';

  readonly displayUnit = 'dega';

  error: Error | null = null;

  private abortController = new AbortController();

  private subscribed = false;

  pointsRaw: CurvePoint[] = [];

  curveData: CurveInfo;

  logCurve: LogCurve;

  color: CurvePaletteKey;

  isVisible: boolean;

  pointsQuantity: number;

  temporaryData: TemporarySettings;

  timeGap: number | null = null;

  subscribeHandler = (value: SocketCurveMessage) => {
    switch (value.msgType) {
      case 'POINT':
        if (this.abortController.signal.aborted || typeof value.value !== 'number' || !Number.isFinite(value.value)) {
          return;
        }
        this.error = null;
        runInAction(() => {
          const newPoint = this.normalizeAngle(value.value as number);

          if (this.lastPoint) {
            this.timeGap = value.key - this.lastPoint.key;
          }
          this.addPoint({ value: newPoint, key: value.key });
        });
        break;
      default:
        break;
    }
  };

  constructor(
    curveData: CurveInfo,
    logCurve: LogCurve,
    type: 'gravity' | 'magnetic',
    private isAltNames: boolean,
    private statePublisher: DirectionalDrillingStatePublisher,
    initState: ToolSettings | null,
    defaultColor: CurvePaletteKey,
    private stompPublisher: WellLogWidgetStompPublisher,
    isPaused: boolean,
    private curvesCacheService: CurvesCacheService,
  ) {
    makeAutoObservable(this, {
      saveToPublisher: action.bound,
      subscribeSocket: action.bound,
      pointsRaw: observable.ref,
      getPoints: action.bound,
    });
    this.type = type;
    this.logCurve = logCurve;
    this.curveData = curveData;
    this.isVisible = initState?.isVisible ?? true;
    this.color = initState?.color ?? defaultColor;
    this.pointsQuantity = initState?.pointsAmount || MIN_POINTS_QUANTITY;

    this.temporaryData = {
      color: this.color,
      isVisible: this.isVisible,
      pointsAmount: this.pointsQuantity,
    };
    if (!isPaused) {
      this.subscribeSocket();
    }
    void this.getPoints();
  }

  async getPoints() {
    try {
      const response = await this.curvesCacheService.getTimeCoordsAsLinear(
        this.logCurve.goStreamId,
        undefined,
        undefined,
        undefined,
        this.abortController,
      );
      this.abortController.signal.throwIfAborted();

      if (Array.isArray(response)) {
        runInAction(() => {
          const allPoints = response.flatMap(CurvesCacheService.linearSubChunkToPoints);

          for (let i = allPoints.length - 1; i >= 0; i--) {
            if (this.pointsRaw.length === MAX_POINTS_QUANTITY) {
              break;
            }

            const convertedValue = this.normalizeAngle(allPoints[i].value);

            if (i === allPoints.length - 1 || convertedValue !== this.normalizeAngle(allPoints[i + 1].value)) {
              this.addPoint({
                key: allPoints[i].key,
                value: convertedValue,
              });
            }
          }

          const lastPoint = allPoints.at(-1);
          const preLastPoint = allPoints.at(-2);
          if (lastPoint && preLastPoint) {
            this.timeGap = lastPoint.key - preLastPoint.key;
          }
        });
      }
    } catch (e: unknown) {
      if (!this.abortController.signal.aborted) {
        runInAction(() => {
          this.error = e instanceof Error ? e : new Error(String(e));
        });
      }
    }
  }

  private normalizeAngle(value: number) {
    // Convert before rounding to retain small changes in radian data.
    const degrees = this.logCurve.unit.abbreviation === 'rad' ? radiansToDegrees(value) : value;
    // Keep floating-point noise (for example, 29.999999999999996) from losing a tenth.
    return roundNumber(degrees + Math.abs(degrees) * Number.EPSILON, 1);
  }

  get newPointGap() {
    if (this.lastPoint && this.preLastPoint) {
      return this.lastPoint.key - this.preLastPoint.key;
    }
    return null;
  }

  get points() {
    return this.pointsRaw.map((pR) => pR.value).slice(-this.pointsQuantity);
  }

  get lastValue(): number | null {
    return this.points.at(-1) ?? null;
  }

  get lastPoint(): CurvePoint | null {
    return this.pointsRaw.at(-1) ?? null;
  }

  get preLastPoint(): CurvePoint | null {
    return this.pointsRaw.at(-2) ?? null;
  }

  get toolName() {
    return this.isAltNames && this.logCurve.parameter.generalName
      ? this.logCurve.parameter.generalName
      : this.logCurve.parameter.displayMnemonic;
  }

  get convertedTimeGap(): number | null {
    return this.timeGap != null ? millisecondsToSeconds(this.timeGap) : null;
  }

  get convertedNewPointGap(): number | null {
    return this.newPointGap != null ? millisecondsToSeconds(this.newPointGap) : null;
  }

  addPoint(pointRaw: CurvePoint) {
    if (pointRaw.value !== this.lastValue) {
      this.pointsRaw = this.pointsRaw.concat(pointRaw).sort((a, b) => a.key - b.key);
    }
    if (this.pointsRaw.length > MAX_POINTS_QUANTITY) {
      this.pointsRaw = this.pointsRaw.slice(1);
    }
  }

  setAltNames(value: boolean) {
    this.isAltNames = value;
  }

  setIsVisible(value: boolean) {
    this.isVisible = value;
  }

  setColor(color: CurvePaletteKey) {
    this.color = color;
  }

  setPointsQuantity(value: number) {
    this.pointsQuantity = value;
  }

  setTemporarySettings(data: Partial<TemporarySettings>) {
    set(this.temporaryData, data);
  }

  saveToPublisher() {
    const typeParams = this.type === 'gravity' ? 'gravityToolParams' : 'magneticToolParams';
    this.statePublisher.setParams({
      [typeParams]: {
        isVisible: this.isVisible,
        color: this.color,
        pointsAmount: this.pointsQuantity,
      },
    });
  }

  saveTemporaryData() {
    this.color = this.temporaryData.color;
    this.isVisible = this.temporaryData.isVisible;
    this.pointsQuantity = this.temporaryData.pointsAmount;
    this.saveToPublisher();
  }

  isStopped(value: boolean) {
    if (value) {
      this.unsubscribeSocket();
    } else {
      this.subscribeSocket();
      this.saveToPublisher();
    }
  }

  subscribeSocket() {
    if (this.subscribed || this.abortController.signal.aborted) {
      return;
    }
    this.subscribed = true;
    this.stompPublisher.subscribe(`/curve/${this.logCurve.goStreamId}`, this.subscribeHandler);
  }

  unsubscribeSocket() {
    if (!this.subscribed) {
      return;
    }
    this.subscribed = false;
    this.stompPublisher.unsubscribe(`/curve/${this.logCurve.goStreamId}`, this.subscribeHandler);
  }

  destroy() {
    this.abortController.abort();
    this.unsubscribeSocket();
  }
}
